/**
 * 会社(クライアント)マスタ(仕様書 §4.2 / kickoff 1-5)。
 * CRUD・price_history・BB業務コード(複数)紐付け。admin=編集可 / viewer=閲覧のみ。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { SearchBox, SortTh, matchKeyword, sortRows, useSort } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import { fmtTs } from '@core/lib/auditLog';
import { formatYen } from '@core/lib/money';
import {
  BUSINESS_LABELS,
  COMPANY_STATUS_LABELS,
  type BusinessType,
  type Company,
  type CompanyStatus,
} from '@core/lib/types';
import { fetchCompanies } from './companyRepo';
import CompanyEditModal from './CompanyEditModal';

type SortKey = 'id' | 'name' | 'unit_price' | 'product_type' | 'delivery_timing' | 'codes' | 'phone' | 'status' | 'created_at';

const STATUS_OPTIONS = (Object.keys(COMPANY_STATUS_LABELS) as CompanyStatus[]).map((key) => ({ key, label: COMPANY_STATUS_LABELS[key] }));
const BUSINESS_OPTIONS = (Object.keys(BUSINESS_LABELS) as BusinessType[]).map((key) => ({ key, label: BUSINESS_LABELS[key] }));

const tsMillis = (ts: unknown): number =>
  ts && typeof (ts as { toMillis?: () => number }).toMillis === 'function' ? (ts as { toMillis: () => number }).toMillis() : 0;

export default function CompaniesPage() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Set<CompanyStatus>>(() => new Set<CompanyStatus>(['active']));
  const [productTypes, setProductTypes] = useState<Set<BusinessType>>(() => new Set());
  const [keyword, setKeyword] = useState('');
  const { sort, toggle } = useSort<SortKey>();
  const [editing, setEditing] = useState<Company | 'new' | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setCompanies(await fetchCompanies());
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const rows = companies
      .filter((c) => matchesFilter(statuses, c.status))
      .filter((c) => matchesFilter(productTypes, c.product_type))
      .filter((c) => matchKeyword(keyword, c.name, c.id, c.phone, ...c.bb_business_codes));
    return sortRows(rows, sort, (c, key) => {
      switch (key) {
        case 'id':
          return c.id;
        case 'name':
          return c.name;
        case 'unit_price':
          return c.unit_price;
        case 'product_type':
          return BUSINESS_LABELS[c.product_type];
        case 'delivery_timing':
          return c.delivery_timing || null;
        case 'codes':
          return c.bb_business_codes.length > 0 ? c.bb_business_codes.join(', ') : null;
        case 'phone':
          return c.phone || null;
        case 'status':
          return c.status === 'active' ? 0 : 1;
        case 'created_at':
          return tsMillis(c.created_at);
      }
    });
  }, [companies, statuses, productTypes, keyword, sort]);

  return (
    <div className="screen">
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          会社一覧
          <span className="hint">{filtered.length}社</span>
          {isAdmin && (
            <button className="btn-line" style={{ marginLeft: 'auto' }} onClick={() => setEditing('new')}>
              ＋新規登録
            </button>
          )}
        </h3>

        <div className="toolbar" style={{ marginBottom: 12 }}>
          <div className="tool-filter">
            <MultiSelect label="ステータス" options={STATUS_OPTIONS} value={statuses} onChange={setStatuses} />
            <MultiSelect label="商材" options={BUSINESS_OPTIONS} value={productTypes} onChange={setProductTypes} />
            <SearchBox value={keyword} onChange={setKeyword} placeholder="社名・ID・BB業務コード・電話番号で検索" style={{ minWidth: 220 }} />
          </div>
        </div>

        {loading ? (
          <div className="empty">読み込み中…</div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            該当する会社がありません
            {companies.length === 0 && '(CSVインポートまたは新規登録でデータを投入してください)'}
          </div>
        ) : (
          <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <SortTh label="ID" sortKey="id" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="社名" sortKey="name" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="会社単価" sortKey="unit_price" sort={sort} onSort={toggle} className="num" />
                <SortTh label="商材" sortKey="product_type" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="納品タイミング" sortKey="delivery_timing" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="BB業務コード" sortKey="codes" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="電話番号" sortKey="phone" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="ステータス" sortKey="status" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="登録日" sortKey="created_at" sort={sort} onSort={toggle} />
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  <td className="hint">{c.id}</td>
                  <td><b>{c.name}</b></td>
                  <td className="num">{formatYen(c.unit_price)}円</td>
                  <td>
                    <span className={`badge ${c.product_type === 'kyutoki' ? 'b-plan' : 'b-pend'}`}>
                      {BUSINESS_LABELS[c.product_type]}
                    </span>
                  </td>
                  <td>{c.delivery_timing || '—'}</td>
                  <td>
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                      {c.bb_business_codes.length === 0
                        ? '—'
                        : c.bb_business_codes.map((code) => (
                            <span key={code} className="badge" style={{ background: '#EEF2F8', color: 'var(--muted)' }}>
                              {code}
                            </span>
                          ))}
                    </span>
                  </td>
                  <td>{c.phone ?? '—'}</td>
                  <td>
                    <span className={`badge ${c.status === 'active' ? 'b-ok' : 'b-ng'}`}>
                      {COMPANY_STATUS_LABELS[c.status]}
                    </span>
                  </td>
                  <td className="hint">{fmtTs(c.created_at, false)}</td>
                  {isAdmin && (
                    <td>
                      <button className="txn-link" onClick={() => setEditing(c)}>
                        編集
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {editing && (
        <CompanyEditModal
          company={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}
